import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Lock, Eye, EyeOff, ShieldCheck, Search, Mail, Trash2, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, Badge, ProgressTrack, Logo } from "../components/kit";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLAN_LABELS = {
  catalyst_free: "Catalyst Free",
  catalyst_pro: "Catalyst Pro",
  catalyst_growth: "Catalyst Growth",
  catalyst_scale: "Catalyst Scale",
  catalyst_custom: "Catalyst Custom",
};

export default function Account() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [schema, setSchema] = useState(null);
  const [state, setState] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/onboarding/schema"),
      api.get("/onboarding/state").catch(() => ({ data: {} })),
    ])
      .then(([s, st]) => { setSchema(s.data); setState(st.data || {}); })
      .catch(() => setLoadErr("Could not load your profile."));
  }, []);

  const answers = state?.answers || {};
  const deferred = useMemo(() => new Set(state?.deferred || []), [state]);

  const answerValue = (q) => {
    const v = answers[q.id];
    if (deferred.has(q.id)) return { text: "Deferred", tone: "watch", answered: false };
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0))
      return { text: "Not answered", tone: "neutral", answered: false };
    return { text: Array.isArray(v) ? v.join(", ") : String(v), tone: "good", answered: true };
  };

  const allQuestions = useMemo(() => (schema ? schema.groups.flatMap((g) => g.questions) : []), [schema]);
  const total = schema?.total_questions || allQuestions.length || 0;
  const answeredCount = useMemo(
    () => allQuestions.filter((q) => answerValue(q).answered).length,
    [allQuestions, answers, deferred] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const pct = total > 0 ? Math.round((answeredCount / total) * 100) : 0;
  const hasProfile = Object.keys(answers).length > 0 || deferred.size > 0;

  const q = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!schema) return [];
    if (!q) return schema.groups;
    return schema.groups
      .map((g) => ({ ...g, questions: g.questions.filter((qq) => {
        const a = answerValue(qq);
        return qq.label.toLowerCase().includes(q) || a.text.toLowerCase().includes(q);
      }) }))
      .filter((g) => g.questions.length > 0);
  }, [schema, q, answers, deferred]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="buktu-root" data-testid="account-page" style={{ minHeight: "100vh", background: "var(--surface-page)" }}>
      <div className="buktu-wrap" style={{ maxWidth: 820, paddingTop: "var(--space-8)", paddingBottom: "var(--space-20)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
          <Logo height={28} to="vitals" />
          <div style={{ marginLeft: "auto" }}>
            <Button variant="ghost" size="sm" data-testid="account-back-btn" onClick={() => navigate("/dashboard")} iconLeft={<ArrowLeft size={16} />}>Back to dashboard</Button>
          </div>
        </div>

        <h1 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", marginBottom: "var(--space-2)" }}>Account settings</h1>
        <p style={{ font: "var(--type-body)", color: "var(--text-muted)", marginBottom: "var(--space-6)" }}>Review your profile, update your answers and manage your account.</p>

        {/* Profile summary */}
        <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-5)" }} data-testid="account-summary-card">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-4)" }}>
            <span style={{ color: "var(--buktu-green)" }}><ShieldCheck size={20} /></span>
            <h3 style={{ font: "var(--type-heading)" }}>Your account</h3>
            <Badge tone="accent" style={{ marginLeft: "auto" }}>{PLAN_LABELS[user?.plan] || "Vitals Core · Free"}</Badge>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "var(--space-4)" }} className="buktu-account-grid">
            <Summary label="Name" value={user?.name || "—"} />
            <Summary label="Email" value={user?.email || "—"} />
            <Summary label="Timezone" value={user?.timezone || "UTC"} />
            <Summary label="Plan" value={user?.role === "owner" || !user?.role ? "Vitals Core — free, no payment needed" : (PLAN_LABELS[user?.plan] || "Catalyst Free")} />
          </div>

          {/* Completeness meter */}
          {hasProfile && (
            <div style={{ marginTop: "var(--space-6)", borderTop: "1px solid var(--border-hairline)", paddingTop: "var(--space-5)" }} data-testid="account-completeness">
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: 8 }}>
                <span className="buktu-eyebrow">Profile completeness</span>
                <span data-testid="account-completeness-pct" style={{ marginLeft: "auto", font: "var(--type-data)", fontSize: 13, color: pct === 100 ? "var(--status-good)" : "var(--text-body)" }}>{pct}% complete</span>
              </div>
              <ProgressTrack value={pct} tone={pct === 100 ? "good" : pct >= 60 ? "brand" : "watch"} height={10} />
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)" }}>
                  {answeredCount} of {total} answered{deferred.size > 0 ? ` · ${deferred.size} still to finish` : " · all done 🎉"}
                </span>
                {deferred.size > 0 && (
                  <Button size="sm" variant="secondary" data-testid="account-finish-deferred-btn" style={{ marginLeft: "auto" }} onClick={() => navigate("/onboarding")}>Finish deferred</Button>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Profile answers review */}
        <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-5)" }} data-testid="account-review-card">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
            <span style={{ color: "var(--vitals-accent)" }}><Pencil size={18} /></span>
            <h3 style={{ font: "var(--type-heading)" }}>Your profile answers</h3>
            <Button size="sm" data-testid="account-edit-answers-btn" style={{ marginLeft: "auto" }} onClick={() => navigate("/onboarding")}>Edit answers</Button>
          </div>
          <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-5)" }}>A summary of everything you told us. Search for a question, then click “Edit answers” to change it.</p>

          {loadErr && <div style={{ color: "var(--status-risk)", font: "var(--type-body-sm)" }}>{loadErr}</div>}
          {!schema && !loadErr && <div style={{ color: "var(--text-muted)", font: "var(--type-body-sm)" }}>Loading your answers…</div>}

          {schema && !hasProfile && (
            <div style={{ padding: "var(--space-5)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)", textAlign: "center" }}>
              <div style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-4)" }}>You haven't completed your baseline yet.</div>
              <Button size="sm" data-testid="account-start-baseline-btn" onClick={() => navigate("/onboarding")}>Start my baseline</Button>
            </div>
          )}

          {schema && hasProfile && (
            <>
              <div style={{ position: "relative", marginBottom: "var(--space-5)" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-faint)", display: "grid", placeItems: "center" }}><Search size={16} /></span>
                <input
                  data-testid="account-search-input"
                  className="buktu-input"
                  style={{ paddingLeft: 38 }}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search your answers (e.g. margin, price, leads)…"
                />
              </div>

              {filteredGroups.length === 0 ? (
                <div data-testid="account-search-empty" style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", padding: "var(--space-4) 0" }}>No questions match “{query}”.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
                  {filteredGroups.map((g) => (
                    <div key={g.id} data-testid={`account-group-${g.id}`}>
                      <span className="buktu-eyebrow">{g.title}</span>
                      <div style={{ marginTop: "var(--space-3)", borderTop: "1px solid var(--border-hairline)" }}>
                        {g.questions.map((qq) => {
                          const a = answerValue(qq);
                          return (
                            <div key={qq.id} data-testid={`account-answer-${qq.id}`} style={{ display: "flex", gap: "var(--space-4)", padding: "var(--space-3) 0", borderBottom: "1px solid var(--border-hairline)", alignItems: "flex-start" }}>
                              <span style={{ flex: 1, font: "var(--type-body-sm)", color: "var(--text-body)" }}>{qq.label}</span>
                              <span style={{ flexShrink: 0, maxWidth: 260, textAlign: "right" }}>
                                {a.answered
                                  ? <span style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{a.text}</span>
                                  : <Badge tone={a.tone}>{a.text}</Badge>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>

        {user?.role === "owner" && <PrivacyCard user={user} onChanged={refreshUser} />}
        <ChangeEmailCard currentEmail={user?.email} />
        <ChangePasswordCard onChanged={refreshUser} />
        <DeleteAccountCard onDeleted={async () => { await logout(); navigate("/"); }} />
      </div>
      <style>{`@media (max-width:640px){.buktu-account-grid{grid-template-columns:1fr !important;}}`}</style>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="buktu-eyebrow">{label}</span>
      <span style={{ font: "var(--type-label)", color: "var(--text-strong)", wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

function PrivacyCard({ user, onChanged }) {
  const [level, setLevel] = useState(user?.coach_privacy || "anonymous");
  const [saving, setSaving] = useState(false);
  const hasCoach = !!user?.coach_id;
  const OPTIONS = [
    { id: "full", title: "Full", desc: "Share your score, dimension breakdown and weekly activity." },
    { id: "score_only", title: "Score only", desc: "Share just your overall score — no breakdown or activity." },
    { id: "anonymous", title: "Anonymous", desc: "Only counted in cohort averages — your name and score stay hidden." },
  ];
  const save = async (lvl) => {
    const prev = level;
    setLevel(lvl); setSaving(true);
    try {
      await api.post("/account/privacy", { coach_privacy: lvl });
      toast.success("Privacy updated", { description: OPTIONS.find((o) => o.id === lvl)?.title });
      if (onChanged) await onChanged();
    } catch (e) {
      setLevel(prev);
      toast.error("Couldn't update your privacy setting");
    } finally { setSaving(false); }
  };
  return (
    <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-5)" }} data-testid="account-privacy-card">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
        <span style={{ color: "var(--buktu-green)" }}><ShieldCheck size={18} /></span>
        <h3 style={{ font: "var(--type-heading)" }}>Coach privacy</h3>
      </div>
      <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-5)" }}>
        {hasCoach
          ? "Control what your coach can see about you in their weekly report."
          : "You're not connected to a coach yet. This controls what a coach would see if you join a cohort."}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", maxWidth: 480 }}>
        {OPTIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            className="buktu-option"
            data-selected={level === o.id}
            data-testid={`privacy-opt-${o.id}`}
            disabled={saving}
            onClick={() => save(o.id)}
            style={{ width: "auto", alignItems: "flex-start", textAlign: "left", padding: "var(--space-3) var(--space-4)" }}
          >
            <span style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, marginTop: 2, border: "2px solid " + (level === o.id ? "var(--buktu-green)" : "var(--border-strong)"), background: level === o.id ? "var(--buktu-green)" : "transparent", display: "grid", placeItems: "center", color: "#fff" }}>{level === o.id && <Check size={11} />}</span>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{o.title}</span>
              <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>{o.desc}</span>
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

function ChangeEmailCard({ currentEmail }) {  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/request-email-change", { new_email: email, current_password: password });
      const dest = data?.new_email || email.toLowerCase().trim();
      setSentTo(dest);
      toast.success("Confirmation link sent", { description: `Check ${dest} and click the link to finish the change.` });
      setEmail(""); setPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not start the email change.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-5)" }} data-testid="account-email-card">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
        <span style={{ color: "var(--buktu-green)" }}><Mail size={18} /></span>
        <h3 style={{ font: "var(--type-heading)" }}>Change email</h3>
      </div>
      <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-5)" }}>Currently <strong>{currentEmail || "—"}</strong>. We'll email a confirmation link to the new address — your email only changes once you click it.</p>
      {sentTo && (
        <div data-testid="account-email-sent" style={{ padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)", color: "var(--buktu-green-deep)", font: "var(--type-body-sm)", fontSize: 13, marginBottom: "var(--space-4)" }}>
          Confirmation link sent to <strong>{sentTo}</strong>. Your email changes once you click it (the link expires in 1 hour).
        </div>
      )}
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", maxWidth: 420 }}>
        {error && <div data-testid="account-email-error" style={errStyle}>{error}</div>}
        <label style={lbl}>New email
          <input data-testid="account-new-email" className="buktu-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" required />
        </label>
        <label style={lbl}>Confirm with password
          <input data-testid="account-email-password" className="buktu-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
        </label>
        <div><Button type="submit" data-testid="account-email-submit" disabled={saving}>{saving ? "Sending…" : "Send confirmation link"}</Button></div>
      </form>
    </Card>
  );
}

function ChangePasswordCard({ onChanged }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) { setError("Your new password must be at least 6 characters."); return; }
    if (next !== confirm) { setError("Those passwords don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated", { description: "Other devices have been signed out." });
      setCur(""); setNext(""); setConfirm("");
      if (onChanged) await onChanged();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update your password.");
    } finally {
      setSaving(false);
    }
  };

  const inputWrap = { position: "relative", display: "flex", alignItems: "center" };
  const eyeBtn = { position: "absolute", right: 10, background: "none", border: 0, cursor: "pointer", color: "var(--text-faint)", display: "grid", placeItems: "center" };

  return (
    <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-5)" }} data-testid="account-password-card">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
        <span style={{ color: "var(--buktu-green)" }}><Lock size={18} /></span>
        <h3 style={{ font: "var(--type-heading)" }}>Change password</h3>
      </div>
      <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-5)" }}>Updating your password signs you out of all other devices.</p>

      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", maxWidth: 420 }}>
        {error && <div data-testid="account-password-error" style={errStyle}>{error}</div>}
        <label style={lbl}>Current password
          <div style={inputWrap}>
            <input data-testid="account-current-password" className="buktu-input" type={show ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)} placeholder="••••••••" required />
          </div>
        </label>
        <label style={lbl}>New password
          <div style={inputWrap}>
            <input data-testid="account-new-password" className="buktu-input" type={show ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} placeholder="At least 6 characters" required />
            <button type="button" data-testid="account-password-toggle" style={eyeBtn} onClick={() => setShow((s) => !s)} aria-label={show ? "Hide passwords" : "Show passwords"}>
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
        <label style={lbl}>Confirm new password
          <div style={inputWrap}>
            <input data-testid="account-confirm-password" className="buktu-input" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password" required />
          </div>
        </label>
        <div><Button type="submit" data-testid="account-password-submit" disabled={saving}>{saving ? "Updating…" : "Update password"}</Button></div>
      </form>
    </Card>
  );
}

function DeleteAccountCard({ onDeleted }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmWord, setConfirmWord] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (confirmWord.trim().toUpperCase() !== "DELETE") { setError('Type DELETE to confirm.'); return; }
    setDeleting(true);
    try {
      await api.post("/auth/delete-account", { password });
      toast.success("Account deleted", { description: "Your data has been removed. Sorry to see you go." });
      if (onDeleted) await onDeleted();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete your account.");
      setDeleting(false);
    }
  };

  return (
    <Card padding="var(--space-6)" data-testid="account-delete-card" style={{ border: "1px solid var(--status-risk-soft)", background: "var(--status-risk-soft)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
        <span style={{ color: "var(--status-risk)" }}><AlertTriangle size={18} /></span>
        <h3 style={{ font: "var(--type-heading)", color: "#8a2a1a" }}>Danger zone</h3>
      </div>
      <p style={{ font: "var(--type-body-sm)", color: "#8a2a1a", marginBottom: "var(--space-5)" }}>Deleting your account permanently removes your profile, score history, check-ins and all associated data. This can't be undone.</p>

      {!open ? (
        <Button variant="accent" data-testid="account-delete-open-btn" onClick={() => setOpen(true)} iconLeft={<Trash2 size={16} />} style={{ background: "var(--status-risk)", borderColor: "var(--status-risk)" }}>Delete my account</Button>
      ) : (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", maxWidth: 420 }}>
          {error && <div data-testid="account-delete-error" style={errStyle}>{error}</div>}
          <label style={lbl}>Confirm with password
            <input data-testid="account-delete-password" className="buktu-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
          </label>
          <label style={lbl}>Type DELETE to confirm
            <input data-testid="account-delete-confirm" className="buktu-input" type="text" value={confirmWord} onChange={(e) => setConfirmWord(e.target.value)} placeholder="DELETE" />
          </label>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <Button type="button" variant="secondary" data-testid="account-delete-cancel-btn" onClick={() => { setOpen(false); setError(""); setPassword(""); setConfirmWord(""); }}>Cancel</Button>
            <Button type="submit" variant="accent" data-testid="account-delete-submit" disabled={deleting} style={{ background: "var(--status-risk)", borderColor: "var(--status-risk)" }}>{deleting ? "Deleting…" : "Permanently delete"}</Button>
          </div>
        </form>
      )}
    </Card>
  );
}

const lbl = { display: "flex", flexDirection: "column", gap: "var(--space-2)", font: "var(--type-label)", color: "var(--text-strong)" };
const errStyle = { padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--paper-white, #fff)", color: "#8a2a1a", font: "var(--type-body-sm)", fontSize: 13, border: "1px solid var(--status-risk)" };
